import type { DnsRecord } from '@troia-v3/types';

const stripRoot = (name: string): string => (name === '@' ? '' : name);

/**
 * Quando o dominio cadastrado ja e um subdominio (ex.: comercial.verden.agr.br),
 * o provedor devolve os nomes relativos ao dominio RAIZ: "send.comercial",
 * "links.comercial", "comercial". Devolve esse trecho repetido ("comercial"),
 * ou '' quando os nomes sao relativos ao proprio dominio.
 *
 * A prova vem do conjunto: so vale se TODOS os registros terminam no trecho.
 * Olhar um registro so erraria num dominio raiz chamado "send.com.br", cujo
 * registro "send" nao e o dominio repetido.
 */
export function sharedSubdomainLabel(records: Pick<DnsRecord, 'name'>[], domain: string): string {
  const labels = domain.split('.');
  // Sobram pelo menos 2 rotulos para o dominio raiz (ex.: "verden.agr").
  for (let size = labels.length - 2; size > 0; size--) {
    const head = labels.slice(0, size).join('.');
    const everyRecordEndsWithHead = records.length > 0 && records.every((record) => {
      const name = stripRoot(record.name);
      return name === head || name.endsWith(`.${head}`);
    });
    if (everyRecordEndsWithHead) return head;
  }
  return '';
}

/** Nome completo do registro, do jeito que o cliente digita no provedor de DNS. */
export function fullRecordName(name: string, domain: string | undefined, sharedLabel = ''): string {
  if (!domain) return name;
  const relative = stripRoot(name);
  if (relative === '') return domain;
  if (relative === domain || relative.endsWith(`.${domain}`)) return relative;
  if (sharedLabel !== '') {
    if (relative === sharedLabel) return domain;
    if (relative.endsWith(`.${sharedLabel}`)) {
      return `${relative.slice(0, -(sharedLabel.length + 1))}.${domain}`;
    }
  }
  return `${relative}.${domain}`;
}

export type TrackingDnsState = 'none' | 'pending' | 'verified';

/**
 * Situacao do endereco de rastreamento no DNS do cliente.
 * - none: dominio sem registro de rastreamento (dominios antigos rastreiam sem ele)
 * - pending: o registro existe e ainda nao foi verificado — o provedor NAO rastreia
 * - verified: tudo certo
 */
export function trackingDnsState(records: Pick<DnsRecord, 'record' | 'status'>[]): TrackingDnsState {
  const tracking = records.filter((record) => record.record === 'TRACKING' || record.record === 'TRACKING_CAA');
  if (tracking.length === 0) return 'none';
  return tracking.every((record) => record.status === 'verified') ? 'verified' : 'pending';
}
